import {
    createContext,
    FormEvent,
    memo,
    MutableRefObject,
    useEffect,
    useRef,
    useState,
} from 'react';
import cn from 'classnames';
import { default as ReactGA4 } from 'react-ga4';
import { useBoolean, useDebounce } from 'react-use';
import { useRouter } from 'next/router';
import { twMerge } from 'tailwind-merge';
import { ArrayUtils, StringUtils } from 'src/utils';
import { useOutsideClick, useFocus } from 'src/hooks';
import { useAsyncFn } from 'src/hooks/useAsyncFn';
import { NO_FOCUS } from 'src/hooks/useFocus';
import { SearchServices } from 'src/services/SearchService';
import { Container, Element, IconButton } from 'src/components';
import SearchDropdown from './SearchDropdown';


const DEFAULT_SUGGEST = [] as any;

export const SearchDropdownContext = createContext(0);

function Search() {
    const router = useRouter();
    const routerKeyword = router.query?.keyword || '';
    const searchRef: MutableRefObject<HTMLElement | null> =
        useRef<HTMLElement>(null);
    const [topicState, topicFn] = useAsyncFn(SearchServices.searchTopic);
    const [contentState, contentFn] = useAsyncFn(SearchServices.searchContent);
    const [keyword, setKeyword] = useState<string>('');
    const [show, setShow] = useBoolean(false);
    const [suggest, setSuggest] = useState(DEFAULT_SUGGEST);
    const [isTracking, setTracking] = useState<boolean>(false);
    const currFocus = useFocus(suggest);

    const handleClickOutside = (isInside: boolean) => {
        if (!isInside) {
            setShow(false);
        }
    };

    const searchDebounce = () => {
        if (keyword.length && show) {
            topicFn(keyword);
            contentFn(keyword);
        } else {
            resetSuggest();
        }
    };

    useOutsideClick(searchRef, handleClickOutside);
    useDebounce(searchDebounce, 200, [keyword]);

    useEffect(() => {
        if (routerKeyword) {
            setKeyword(StringUtils.formatQuerySearch(routerKeyword as string));
        }
    }, [routerKeyword]);

    useEffect(() => {
        if (contentState.value && topicState.value) {
            const contents = contentState.value?.article || [];
            const topics =
                topicState.value?.topic?.map((item: any) => ({
                    ...item,
                    isTopic: true,
                })) || [];

            setSuggest([...topics.slice(0, 1), ...contents.slice(0, 3)]);
        }
    }, [contentState.value, topicState.value]);

    const resetSuggest = () => {
        setTimeout(() => {
            setSuggest(DEFAULT_SUGGEST);
        }, 10);
    };

    const clickSubmit = (e: any) => {
        ReactGA4.event({
            category: 'mobile_search',
            action: 'clickButtonSearch',
        });
        handleSubmit(e);
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!keyword) {
            alert('Bạn phải gõ từ khoá tìm kiếm');
            return false;
        }

        if (currFocus !== NO_FOCUS && currFocus < suggest.length) {
            const currItem = suggest[currFocus];

            if (currItem.isTopic) {
                router.push(`/t${currItem.id}.epi`);
            } else {
                router.push(`/c${currItem.id}.epi`);
            }
        } else {
            router.push(
                `/tim-kiem/${StringUtils.formatSearchStr(keyword)}.epi`,
            );
        }
        resetSuggest();
        setShow(false);
        return false;
    };

    const handleInput = (e: FormEvent<HTMLInputElement>) => {
        e.preventDefault();
        setKeyword(e.currentTarget.value);
    };

    const handleFocus = () => {
        setShow(true);
    };

    const handleBlur = (e: any) => {
        if (e.keyCode === 13) {
            e.target.blur();
            handleSubmit(e);
        }
    };

    useEffect(() => {
        if (!ArrayUtils.isEmpty(suggest) && !isTracking) {
            ReactGA4.event({
                category: 'mobile_search',
                action: 'typeSearch',
            });
            setTracking(true);
        }
    }, [suggest, isTracking]);

    useEffect(() => {
        setTracking(false);
    }, [router.asPath]);

    return (
        <SearchDropdownContext.Provider value={currFocus as number}>
            <Element className={cn('search-box', 'pt-[8px] mb-[24px]')}>
                <Container>
                    <form onSubmit={handleSubmit}>
                        <Element
                            domRef={searchRef}
                            className={twMerge(
                                'bm-search-container',
                                'flex relative',
                                'w-full h-[52px]',
                                'ml-0',
                                'pl-[16px] pr-[12px]',
                                'rounded-full',
                                'border border-solid border-[#D9D9D9]',
                                cn({
                                    [cn(
                                        'rounded-t-[12px] rounded-b-[0] border-none',
                                        'shadow-[0_0_16px_0_rgba(0,0,0,0.1)]',
                                    )]: suggest.length > 0 && show,
                                }),
                            )}
                        >
                            <input
                                name="search"
                                type="text"
                                className={cn(
                                    'grow',
                                    'text-[14px] text-[#005654] font-semibold',
                                    'mr-[16px]',
                                    'placeholder:text-[14px] placeholder:font-normal placeholder:text-[#666]',
                                )}
                                placeholder="Nhập nội dung tìm kiếm"
                                onFocus={handleFocus}
                                value={keyword}
                                onChange={handleInput}
                                onKeyDown={handleBlur}
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck="false"
                            />
                            <IconButton
                                className={cn(
                                    'flex items-center justify-center',
                                    'text-[32px] text-[#4D4D4D]',
                                )}
                                onClick={clickSubmit}
                                icon="new-search-regular"
                            />
                            <SearchDropdown
                                keyword={keyword}
                                resetSuggest={resetSuggest}
                                suggest={suggest}
                                show={show}
                            />
                        </Element>
                    </form>
                </Container>
            </Element>
        </SearchDropdownContext.Provider>
    );
}

export default memo(Search);
